import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/AppIcon';
import type { CompensationReportView } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import { appDate } from '@/utils/appDateFormat';

interface CompensationReportTableProps {
  data: CompensationReportView;
  startDate?: string;
  endDate?: string;
}

const CompensationReportTable: React.FC<CompensationReportTableProps> = ({ data, startDate, endDate }) => {
  const { t } = useTranslation('tournament');
  const { format: formatCurrency } = useCurrency();

  if (!data.lines || data.lines.length === 0) {
    const formatDate = (dateStr: string) => appDate(dateStr) || dateStr;

    const dateRangeText = startDate && endDate
      ? t('emptyState.compensationNoDataForRange', { start: formatDate(startDate), end: formatDate(endDate) })
      : t('emptyState.compensationNoData');

    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="compensation-empty">
        <Icon name="FileSearch" size={40} className="mx-auto text-gray-400 dark:text-gray-500 mb-3" />
        <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">{t('emptyState.compensationNoDataTitle')}</h3>
        <p className="text-gray-500 dark:text-gray-400 text-sm">{dateRangeText}</p>
      </div>
    );
  }

  const customRateCount = data.customRateCount ?? data.lines.filter((l) => l.isCustomRate).length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="compensation-report-table">
      {/* Rate legend: the company-wide rule rate once, plus how many lines used a referee-level override */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 border-b border-border bg-muted/30 text-xs text-muted-foreground" data-testid="compensation-rate-legend">
        <span className="inline-flex items-center gap-1.5">
          <Icon name="Coins" size={14} className="text-muted-foreground" />
          {data.ruleRate != null
            ? t('ruleRateLegend', { rate: data.ruleRate })
            : t('No active work point rule — amounts use a 1.0 fallback rate')}
        </span>
        {customRateCount > 0 && (
          <span className="inline-flex items-center gap-1.5" data-testid="compensation-custom-rate-count">
            <span className="inline-block w-2 h-2 rounded-full bg-violet-500" aria-hidden="true" />
            {t('customRateLegend', { count: customRateCount })}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-border">
              <th scope="col" className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                {t('Referee Name')}
              </th>
              <th scope="col" className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                {t('Match Count')}
              </th>
              <th scope="col" className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                {t('Total Work Points')}
              </th>
              <th scope="col" className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                {t('Total Currency Amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr
                key={line.refereeUserId}
                className="border-b border-border last:border-b-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
              >
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    {line.refereeName}
                    {line.isCustomRate && (
                      <span
                        className="inline-flex items-center rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
                        title={t('customRateTooltip', { rate: line.pointToCurrencyRate, ruleRate: line.ruleRate ?? data.ruleRate })}
                        data-testid={`compensation-custom-rate-badge-${line.refereeUserId}`}
                      >
                        {t('Custom rate')}
                      </span>
                    )}
                  </span>
                  {line.pointToCurrencyRate != null && line.pointToCurrencyRate > 0 && (
                    <span className="block text-xs font-normal text-muted-foreground" data-testid={`compensation-rate-${line.refereeUserId}`}>
                      {t('{{rate}} per point', { rate: line.pointToCurrencyRate })}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                  {line.matchCount}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                  {line.totalWorkPoints.toFixed(1)}
                </td>
                <td className="px-4 py-3 font-medium text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(line.totalCurrencyAmount)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 dark:bg-gray-800/50 border-t-2 border-border font-semibold">
              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                {t('Total')}
              </td>
              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                {data.lines.reduce((sum, l) => sum + l.matchCount, 0)}
              </td>
              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                {data.totalWorkPoints.toFixed(1)}
              </td>
              <td className="px-4 py-3 font-bold text-emerald-700 dark:text-emerald-400">
                {formatCurrency(data.totalCurrencyAmount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CompensationReportTable;
